import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom'
import Swal from 'sweetalert2'
import * as authService from '@mepro/services/authService'
import { OpenDWContext } from '@mepro/context/OpenDWContext'
import { useSingleTabEnforcement } from '@mepro/hooks/useSingleTabEnforcement'
import { useSessionReplacedPoll } from '@mepro/hooks/useSessionReplacedPoll'
import { useActivityTracker } from '@mepro/hooks/useActivityTracker'
import { SessionWarningModal } from '@mepro/components/SessionWarningModal'
import ChatWidget from '@mepro/components/ChatWidget'
import DraggableLiveTvFloat from '@mepro/components/DraggableLiveTvFloat'
import DraggablePredictionFloat from '@mepro/components/DraggablePredictionFloat'
import { USER_SUMMARY_UPDATED_EVENT } from '@mepro/services/pointsService'
import type { TenantThemeConfig } from '../types/theme'
import ThemeSwitcher from './ThemeSwitcher'
import HtGoldMarquee from './HtGoldMarquee'
import HtGoldBottomNav from './HtGoldBottomNav'
import HtGoldViewportLayer from './HtGoldViewportLayer'
import HtGoldPaymentRatesModal from './HtGoldPaymentRatesModal'
import { getActiveThemes } from '../config/themes'
import { isMeeduangWordmarkLogo } from '../lib/meeduangBranding'

export interface AssetConfig {
  CDN_BASE_URL: string
  CSS_PATH: string
  IMG_PATH: string
  FONTS_PATH: string
}

interface HtGoldLayoutProps {
  themeConfig: TenantThemeConfig
  assetConfig: AssetConfig
  themeId: string
  onSwitchTheme: (id: string) => void
  showThemeSwitcher?: boolean
}

const HtGoldLayout: React.FC<HtGoldLayoutProps> = ({
  themeConfig,
  themeId,
  onSwitchTheme,
  showThemeSwitcher = true,
}) => {
  const navigate = useNavigate()
  const location = useLocation()
  const authenticated = authService.isAuthenticated()
  const hasAuthSession = authenticated || Boolean(authService.getRefreshToken())
  const { isDuplicateTab, takeOverSession } = useSingleTabEnforcement(authenticated)
  useSessionReplacedPoll(hasAuthSession)

  const [logoOk, setLogoOk] = useState(true)
  const [showPaymentRates, setShowPaymentRates] = useState(false)
  const [userInfoSummary, setUserInfoSummary] = useState<authService.UserInfoSummary | null>(null)
  const accessAlertKeyRef = useRef<string | null>(null)
  const themes = getActiveThemes()
  const isHome = location.pathname === '/' || location.pathname === ''
  const siteName = themeConfig.theme.branding.siteName
  const marqueeText =
    themeConfig.settings?.marqueeText?.trim() || `ยินดีต้อนรับเข้าสู่ ${siteName}`
  const slogan = 'หวยวันนี้ · มั่นคง ซื่อตรง จ่ายจริง'

  const tenantLotteryEnabled = themeConfig.settings?.lotteryEnabled !== false
  const tenantCasinoEnabled = themeConfig.settings?.casinoEnabled !== false
  const userLotteryEnabled = authService.isManagedServiceEnabled(
    userInfoSummary?.serviceConfigs,
    'lottery'
  )
  const userCasinoEnabled = authService.isManagedServiceEnabled(
    userInfoSummary?.serviceConfigs,
    'casino'
  )
  const isLotteryEnabled = tenantLotteryEnabled && userLotteryEnabled
  const isCasinoEnabled = tenantCasinoEnabled && userCasinoEnabled

  useEffect(() => {
    if (!authenticated) {
      setUserInfoSummary(null)
      return
    }
    authService.fetchUserInfoSummary().then(setUserInfoSummary)
  }, [authenticated])

  useEffect(() => {
    const refresh = () => {
      if (authenticated) authService.fetchUserInfoSummary({ force: true }).then(setUserInfoSummary)
    }
    window.addEventListener(USER_SUMMARY_UPDATED_EVENT, refresh)
    window.addEventListener('user_service_access_updated', refresh)
    return () => {
      window.removeEventListener(USER_SUMMARY_UPDATED_EVENT, refresh)
      window.removeEventListener('user_service_access_updated', refresh)
    }
  }, [authenticated])

  // Block lottery/casino routes when tenant or user access is off
  useEffect(() => {
    if (!authenticated) {
      accessAlertKeyRef.current = null
      return
    }
    if (!userInfoSummary) return

    const path = location.pathname || '/'
    const isLotteryPath =
      path === '/lobby-huay' ||
      path.startsWith('/lobby-huay/listall') ||
      path.startsWith('/lobby-huay/lottery-terms') ||
      path.startsWith('/lobby-huay/bet') ||
      path.startsWith('/lobby-huay/yeekee') ||
      path.startsWith('/lobby-huay/set') ||
      path.startsWith('/lobby-huay/win-number')
    const isCasinoPath = path === '/lobby-casino'

    let blockedService: 'lottery' | 'casino' | null = null
    let blockedReason: 'tenant' | 'user' = 'user'
    if (isLotteryPath) {
      if (!tenantLotteryEnabled) {
        blockedService = 'lottery'
        blockedReason = 'tenant'
      } else if (!userLotteryEnabled) {
        blockedService = 'lottery'
        blockedReason = 'user'
      }
    } else if (isCasinoPath) {
      if (!tenantCasinoEnabled) {
        blockedService = 'casino'
        blockedReason = 'tenant'
      } else if (!userCasinoEnabled) {
        blockedService = 'casino'
        blockedReason = 'user'
      }
    }

    if (!blockedService) {
      accessAlertKeyRef.current = null
      return
    }

    const alertKey = `${blockedService}:${blockedReason}:${path}`
    if (accessAlertKeyRef.current === alertKey) return
    accessAlertKeyRef.current = alertKey

    void Swal.fire({
      icon: 'warning',
      title:
        blockedReason === 'tenant'
          ? 'ปิดปรับปรุงระบบ'
          : blockedService === 'lottery'
            ? 'ปิดการใช้งานหวย'
            : 'ปิดการใช้งานเกมส์',
      text:
        blockedReason === 'tenant'
          ? blockedService === 'lottery'
            ? 'ขณะนี้ระบบหวยปิดปรับปรุงชั่วคราว กรุณาลองใหม่อีกครั้งภายหลัง'
            : 'ขณะนี้ระบบเกมส์ปิดปรับปรุงชั่วคราว กรุณาลองใหม่อีกครั้งภายหลัง'
          : blockedService === 'lottery'
            ? 'บัญชีนี้ถูกปิดสิทธิ์การเข้าใช้งานหวยชั่วคราว กรุณาติดต่อแอดมิน'
            : 'บัญชีนี้ถูกปิดสิทธิ์การเข้าใช้งานเกมส์ชั่วคราว กรุณาติดต่อแอดมิน',
      confirmButtonText: 'ตกลง',
      backdrop: 'rgba(20, 12, 8, 0.86)',
      showClass: { popup: 'mepro-swal-show' },
      hideClass: { popup: 'mepro-swal-hide' },
      customClass: {
        container: 'mepro-swal-container',
        popup: 'mepro-swal-popup',
        title: 'mepro-swal-title',
        htmlContainer: 'mepro-swal-html',
        confirmButton: 'mepro-swal-confirm',
        actions: 'mepro-swal-actions',
      },
    }).finally(() => {
      navigate('/dashboard', { replace: true })
    })
  }, [
    authenticated,
    location.pathname,
    navigate,
    userInfoSummary,
    tenantLotteryEnabled,
    tenantCasinoEnabled,
    userLotteryEnabled,
    userCasinoEnabled,
  ])

  const showAccessBlocked = useCallback(
    (service: 'lottery' | 'casino') => {
      const tenantOk = service === 'lottery' ? tenantLotteryEnabled : tenantCasinoEnabled
      const reason: 'tenant' | 'user' = tenantOk ? 'user' : 'tenant'
      void Swal.fire({
        icon: 'warning',
        title:
          reason === 'tenant'
            ? 'ปิดปรับปรุงระบบ'
            : service === 'lottery'
              ? 'ปิดการใช้งานหวย'
              : 'ปิดการใช้งานเกมส์',
        text:
          reason === 'tenant'
            ? service === 'lottery'
              ? 'ขณะนี้ระบบหวยปิดปรับปรุงชั่วคราว กรุณาลองใหม่อีกครั้งภายหลัง'
              : 'ขณะนี้ระบบเกมส์ปิดปรับปรุงชั่วคราว กรุณาลองใหม่อีกครั้งภายหลัง'
            : service === 'lottery'
              ? 'บัญชีนี้ถูกปิดสิทธิ์การเข้าใช้งานหวยชั่วคราว กรุณาติดต่อแอดมิน'
              : 'บัญชีนี้ถูกปิดสิทธิ์การเข้าใช้งานเกมส์ชั่วคราว กรุณาติดต่อแอดมิน',
        confirmButtonText: 'ตกลง',
        backdrop: 'rgba(20, 12, 8, 0.86)',
        showClass: { popup: 'mepro-swal-show' },
        hideClass: { popup: 'mepro-swal-hide' },
        customClass: {
          container: 'mepro-swal-container',
          popup: 'mepro-swal-popup',
          title: 'mepro-swal-title',
          htmlContainer: 'mepro-swal-html',
          confirmButton: 'mepro-swal-confirm',
          actions: 'mepro-swal-actions',
        },
      })
    },
    [tenantLotteryEnabled, tenantCasinoEnabled]
  )

  const sessionIdleMinutes = themeConfig.settings?.sessionIdleMinutes ?? 30
  const activityTrackingEnabled = authenticated && sessionIdleMinutes > 0
  const {
    showSessionWarning,
    timeRemaining,
    extendSession,
    handleLogout: sessionWarningLogout,
  } = useActivityTracker({
    inactivityTimeout: sessionIdleMinutes * 60 * 1000,
    warningTimeout: 5 * 60 * 1000,
    enabled: activityTrackingEnabled,
    onLogout: async () => {
      await authService.logout()
      navigate('/login', { replace: true })
    },
    onExtendSession: async () => {
      const refreshed = await authService.refreshToken()
      if (!refreshed.success) throw new Error('Token refresh failed')
    },
  })

  const openDWValue = useMemo(
    () => ({
      openDeposit: () => navigate('/topup'),
      openWithdraw: () => navigate('/withdraw'),
    }),
    [navigate]
  )

  const openPaymentRates = useCallback(() => setShowPaymentRates(true), [])
  const closePaymentRates = useCallback(() => setShowPaymentRates(false), [])

  useEffect(() => {
    setLogoOk(true)
  }, [themeConfig.theme.branding.logoUrl])

  useEffect(() => {
    const onOpen = () => openPaymentRates()
    window.addEventListener('htg-open-payment-rates', onOpen)
    return () => window.removeEventListener('htg-open-payment-rates', onOpen)
  }, [openPaymentRates])

  const handleLogout = async () => {
    try {
      await authService.logout()
    } catch {
      // ignore
    }
    navigate('/', { replace: true })
    window.location.reload()
  }

  const handleQuickBetClick = (e: React.MouseEvent) => {
    if (isLotteryEnabled) return
    e.preventDefault()
    showAccessBlocked('lottery')
  }

  const logoGradDesktop = 'htgLogoGoldDesk'
  const logoGradHome = 'htgLogoGoldHome'
  const useWordmarkLogo = isMeeduangWordmarkLogo(themeConfig.theme.branding.logoUrl)

  const renderBrandLogo = (gradId: string, markSize = 40) => (
    <>
      <span className="htg-brand__logo">
        {logoOk && themeConfig.theme.branding.logoUrl ? (
          <img
            className={`htg-brand__img${useWordmarkLogo ? ' htg-brand__img--wordmark' : ''}`}
            src={themeConfig.theme.branding.logoUrl}
            alt=""
            onError={() => setLogoOk(false)}
          />
        ) : (
          <span className="htg-brand__mark" aria-hidden>
            <svg viewBox="0 0 56 56" width={markSize} height={markSize} fill="none">
              <defs>
                <linearGradient id={gradId} x1="8" y1="6" x2="48" y2="50" gradientUnits="userSpaceOnUse">
                  <stop stopColor="var(--htg-primary-light)" />
                  <stop offset="0.45" stopColor="var(--htg-primary)" />
                  <stop offset="1" stopColor="color-mix(in srgb, var(--htg-primary) 70%, #8a5a00)" />
                </linearGradient>
              </defs>
              <rect
                x="3"
                y="3"
                width="50"
                height="50"
                rx="14"
                fill="color-mix(in srgb, var(--htg-dark) 80%, #000)"
                stroke={`url(#${gradId})`}
                strokeWidth="2.2"
              />
              <path
                d="M16 38V18h5.2l4.1 12.4L29.4 18H34.6v20h-4.2V25.2L26.6 38h-3.2l-3.8-12.8V38H16zm20.2 0V18h12.6v3.6H40.4v4.4h6.8v3.5H40.4V34.4h8.6V38H36.2z"
                fill={`url(#${gradId})`}
              />
            </svg>
          </span>
        )}
      </span>
      {!useWordmarkLogo ? (
        <span className="htg-brand__meta">
          <span className="htg-brand__name">{siteName}</span>
        </span>
      ) : null}
    </>
  )

  const lineIcon = (
    <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden>
      <path
        fill="currentColor"
        d="M19.7 9.7c0-3.7-3.7-6.7-8.3-6.7S3.1 6 3.1 9.7c0 3.3 3 6.1 7 6.6.27.06.64.17.73.4.08.21.05.54.03.76l-.12.74c-.04.22-.17.87.76.48s5.08-3 6.93-5.13c1.28-1.38 1.97-2.78 1.97-4.35z"
      />
    </svg>
  )

  return (
    <OpenDWContext.Provider value={openDWValue}>
    <div className={`htg-shell${authenticated ? '' : ' htg-shell--guest'}`}>
      {authenticated && isDuplicateTab ? (
        <div className="htg-tab-lock" style={{ zIndex: 999999 }}>
          <div className="htg-tab-lock__card">
            <h2>มีการใช้งานจากอีกแท็บหนึ่งอยู่</h2>
            <p>กรุณาใช้แค่หนึ่งแท็บ หรือกดปุ่มด้านล่างเพื่อใช้แท็บนี้</p>
            <button type="button" onClick={takeOverSession} className="htg-tab-lock__btn">
              ใช้แท็บนี้แทน
            </button>
          </div>
        </div>
      ) : null}

      {/* Desktop header */}
      <header
        className={`htg-header htg-header--desktop${authenticated ? '' : ' htg-header--guest'}${isHome ? ' htg-header--home' : ''}`}
      >
        <span className="htg-header__shine" aria-hidden />
        <Link to="/" className="htg-brand" aria-label={siteName}>
          {renderBrandLogo(logoGradDesktop)}
        </Link>
        <div className="htg-header__right">
          <button
            type="button"
            className="htg-btn-rates"
            onClick={openPaymentRates}
            aria-label="อัตราการจ่าย"
          >
            <svg viewBox="0 0 24 24" width="15" height="15" aria-hidden>
              <path
                fill="currentColor"
                d="M12 2.5 14.8 9l6.7.6-5.1 4.4 1.6 6.5L12 17.4 6 20.5l1.6-6.5L2.5 9.6 9.2 9z"
              />
            </svg>
            <span className="htg-btn-rates__label">
              <span className="htg-btn-rates__long">อัตราการจ่าย</span>
              <span className="htg-btn-rates__short">อัตราจ่าย</span>
            </span>
          </button>

          {authenticated ? (
            <button
              type="button"
              className="htg-btn-ghost"
              onClick={() => void handleLogout()}
              title="ออกจากระบบ"
              aria-label="ออกจากระบบ"
            >
              <span className="htg-btn-ghost__long">ออกจากระบบ</span>
              <span className="htg-btn-ghost__short">ออก</span>
            </button>
          ) : (
            <Link to="/register" className="htg-btn-login htg-header__register">
              <span className="htg-btn-login__long">สมัครสมาชิก</span>
              <span className="htg-btn-login__short">สมัคร</span>
            </Link>
          )}

          <Link to="/contact" className="htg-line" title="ติดต่อเรา" aria-label="ติดต่อเรา">
            {lineIcon}
            <span className="htg-line__label">
              <span className="htg-line__label-long">ติดต่อเรา</span>
              <span className="htg-line__label-short">ติดต่อ</span>
            </span>
          </Link>
        </div>
      </header>

      {/* Mobile quickbar — HTD-style 4 tiles */}
      <nav
        className={`htg-quickbar${authenticated ? '' : ' htg-quickbar--guest'}`}
        aria-label="เมนูด่วน"
      >
        <button
          type="button"
          className="htg-quickbar__tile htg-quickbar__tile--rates"
          onClick={openPaymentRates}
        >
          <span className="htg-quickbar__icon" aria-hidden>
            <svg viewBox="0 0 24 24" width="22" height="22">
              <path
                fill="currentColor"
                d="M12 2.5c-3.2 0-5.8 1.2-5.8 2.7v1.1c0 1.5 2.6 2.7 5.8 2.7s5.8-1.2 5.8-2.7V5.2C17.8 3.7 15.2 2.5 12 2.5zm-5.8 6.3v2.2c0 1.5 2.6 2.7 5.8 2.7s5.8-1.2 5.8-2.7V8.8c-1.2 1-3.3 1.6-5.8 1.6S7.4 9.8 6.2 8.8zm0 4.4v2.2c0 1.5 2.6 2.7 5.8 2.7s5.8-1.2 5.8-2.7v-2.2c-1.2 1-3.3 1.6-5.8 1.6s-4.6-.6-5.8-1.6zm0 4.4V18c0 1.5 2.6 2.7 5.8 2.7s5.8-1.2 5.8-2.7v-.7c-1.2 1-3.3 1.6-5.8 1.6s-4.6-.6-5.8-1.6z"
              />
            </svg>
          </span>
          <span className="htg-quickbar__label">อัตราจ่าย</span>
        </button>

        <Link to="/contact" className="htg-quickbar__tile htg-quickbar__tile--line">
          <span className="htg-quickbar__icon" aria-hidden>
            <svg viewBox="0 0 24 24" width="22" height="22">
              <path
                fill="currentColor"
                d="M12 3.4C7.2 3.4 3.3 6.7 3.3 10.7c0 3.6 3.2 6.6 7.5 7.2.3.06.7.18.8.42.08.22.05.56.03.8l-.13.78c-.04.24-.18.92.8.5 3.5-2.05 6-5.1 6-7.5 0-4-3.9-7.3-8.7-7.3z"
              />
            </svg>
          </span>
          <span className="htg-quickbar__label">ติดต่อ</span>
        </Link>

        <Link
          to="/lobby-huay/listall"
          className="htg-quickbar__tile htg-quickbar__tile--bet"
          onClick={handleQuickBetClick}
        >
          <span className="htg-quickbar__icon" aria-hidden>
            <svg viewBox="0 0 24 24" width="22" height="22">
              <path
                fill="currentColor"
                d="M6.2 4.4h8.4c.7 0 1.3.3 1.7.9l2.3 3.4c.3.45.3 1.05 0 1.5l-5.6 8.2c-.4.6-1.1.95-1.8.95H4.8c-.9 0-1.6-.9-1.3-1.75L6.2 4.4zm1.7 2.1-.9 3.3h3.7l.9-3.3H7.9zm-.3 4.8-.9 3.3h3.7l.9-3.3H7.6zm5.5-4.8-.9 3.3h3.2l1.1-1.65c.1-.15.05-.35-.08-.45L13.1 6.4z"
              />
            </svg>
          </span>
          <span className="htg-quickbar__label">แทงหวย</span>
        </Link>

        {authenticated ? (
          <button
            type="button"
            className="htg-quickbar__tile htg-quickbar__tile--logout"
            onClick={() => void handleLogout()}
            aria-label="ออกจากระบบ"
          >
            <span className="htg-quickbar__icon" aria-hidden>
              <svg viewBox="0 0 24 24" width="22" height="22">
                <path
                  fill="currentColor"
                  d="M10.4 4.6v1.9H6.8v10.9h3.6v1.9H4.9V4.6zm5.1 3.2 1.35 1.35-1.9 1.9H19.5v1.9h-4.55l1.9 1.9-1.35 1.35L12.05 12z"
                />
              </svg>
            </span>
            <span className="htg-quickbar__label">ออก</span>
          </button>
        ) : (
          <Link to="/register" className="htg-quickbar__tile htg-quickbar__tile--register">
            <span className="htg-quickbar__icon" aria-hidden>
              <svg viewBox="0 0 24 24" width="22" height="22">
                <path
                  fill="currentColor"
                  d="M12 3.8a4 4 0 1 1 0 8 4 4 0 0 1 0-8zm0 9.2c3.5 0 6.4 1.7 6.4 3.9V20H5.6v-3.1C5.6 14.7 8.5 13 12 13z"
                />
              </svg>
            </span>
            <span className="htg-quickbar__label">สมัคร</span>
          </Link>
        )}
      </nav>

      <HtGoldMarquee text={marqueeText} />

      {isHome ? (
        <div className="htg-home-brand">
          <Link to="/" className="htg-home-brand__link" aria-label={siteName}>
            {renderBrandLogo(logoGradHome, 52)}
          </Link>
          <p className="htg-home-brand__slogan">{slogan}</p>
        </div>
      ) : null}

      <main className="htg-main">
        <Outlet />
      </main>

      {showThemeSwitcher || authenticated ? (
        <HtGoldViewportLayer>
          {showThemeSwitcher ? (
            <ThemeSwitcher themeId={themeId} themes={themes} onSwitch={onSwitchTheme} />
          ) : null}
          {authenticated ? (
            <HtGoldBottomNav
              authenticated
              isLotteryEnabled={isLotteryEnabled}
              isCasinoEnabled={isCasinoEnabled}
              onBlockedAccess={showAccessBlocked}
            />
          ) : null}
        </HtGoldViewportLayer>
      ) : null}

      <HtGoldPaymentRatesModal
        isOpen={showPaymentRates}
        onClose={closePaymentRates}
        content={themeConfig.settings.paymentRates}
      />

      {authenticated && themeConfig.settings?.webChatEnabled === true ? (
        <ChatWidget themeConfig={themeConfig} />
      ) : null}
      {authenticated ? <DraggableLiveTvFloat themeConfig={themeConfig} bottomOffset={96} /> : null}
      {authenticated ? (
        <DraggablePredictionFloat themeConfig={themeConfig} bottomOffset={96} />
      ) : null}
      {authenticated ? (
        <SessionWarningModal
          isOpen={showSessionWarning}
          timeRemaining={timeRemaining}
          onExtendSession={extendSession}
          onLogout={sessionWarningLogout}
        />
      ) : null}
    </div>
    </OpenDWContext.Provider>
  )
}

export default HtGoldLayout
